import { now } from "@internationalized/date";
import z from "zod";

import { getDefaultTimeZone } from "./dates";

/**
 * Juniors age divisions, by high school graduation year. `true` means "all
 * juniors" rather than a specific division.
 *
 * Lives here rather than in a tRPC router so both the server procedures and the
 * routes' search-param validation can import it without pulling server code
 * into the client bundle.
 */
export const juniorsDivisionSchema = z.xor([
  z.literal(12),
  z.literal(14),
  z.literal(16),
  z.literal(18),
  z.literal(true),
]);

export type JuniorsDivision = z.infer<typeof juniorsDivisionSchema>;

/**
 * The graduation year that counts as 18U right now.
 *
 * Divisions roll over on September 1st, so from 9/1 onward the base year is
 * next calendar year. Takes an explicit date so the rollover is testable
 * without mocking the clock — `month` is 1-based.
 *
 * The default is evaluated in the default timezone, not the runtime's local
 * one, so SSR and CSR agree and the rollover flips at the same instant for
 * everyone. `new Date()` here would put a UTC server and a PT browser on
 * different base years around the 9/1 boundary.
 */
export function getJuniorsBaseYear(
  date: { year: number; month: number } = now(getDefaultTimeZone()),
): number {
  return date.year + (date.month >= 9 ? 1 : 0);
}

/**
 * Minimum graduation year for a juniors division: 18U is the base year, and
 * each younger division is two years further out.
 */
export function getMinGraduationYear(
  division: JuniorsDivision,
  date?: { year: number; month: number },
): number {
  const baseYear = getJuniorsBaseYear(date);

  if (division === true || division === 18) return baseYear;
  if (division === 16) return baseYear + 2;
  if (division === 14) return baseYear + 4;
  return baseYear + 6; // 12U
}
